import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function TimeValueMoneyCalculator() {
  const [pv, setPv] = useState("");
  const [fv, setFv] = useState("");
  const [rate, setRate] = useState("");
  const [periods, setPeriods] = useState("");
  const [pmt, setPmt] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateFV = () => {
    const presentValue = parseFloat(pv);
    const interestRate = parseFloat(rate) / 100;
    const n = parseFloat(periods);
    const payment = parseFloat(pmt) || 0;

    if (isNaN(presentValue) || isNaN(interestRate) || isNaN(n)) {
      toast.error("Please enter valid values");
      return;
    }

    // FV = PV(1+r)^n + PMT[((1+r)^n - 1)/r]
    const futureValue = presentValue * Math.pow(1 + interestRate, n);
    const annuityFV = payment * ((Math.pow(1 + interestRate, n) - 1) / interestRate);
    
    setResult(futureValue + annuityFV);
    toast.success("Future Value calculated!");
  };

  const calculatePV = () => {
    const futureValue = parseFloat(fv);
    const interestRate = parseFloat(rate) / 100;
    const n = parseFloat(periods);
    const payment = parseFloat(pmt) || 0;

    if (isNaN(futureValue) || isNaN(interestRate) || isNaN(n)) {
      toast.error("Please enter valid values");
      return;
    }

    // PV = FV / (1+r)^n + PMT[1 - (1+r)^-n]/r
    const presentValue = futureValue / Math.pow(1 + interestRate, n);
    const annuityPV = payment * ((1 - Math.pow(1 + interestRate, -n)) / interestRate);
    
    setResult(presentValue + annuityPV);
    toast.success("Present Value calculated!");
  };

  const reset = () => {
    setPv("");
    setFv("");
    setRate("");
    setPeriods("");
    setPmt("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Time Value of Money Calculator</CardTitle>
          <CardDescription>
            Comprehensive TVM calculations for PV, FV, and annuities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs defaultValue="fv">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="fv">Calculate FV</TabsTrigger>
              <TabsTrigger value="pv">Calculate PV</TabsTrigger>
            </TabsList>
            
            <TabsContent value="fv" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pv">Present Value ($)</Label>
                <Input
                  id="pv"
                  type="number"
                  placeholder="10000"
                  value={pv}
                  onChange={(e) => setPv(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate1">Interest Rate (% per period)</Label>
                <Input
                  id="rate1"
                  type="number"
                  placeholder="5"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="periods1">Number of Periods</Label>
                <Input
                  id="periods1"
                  type="number"
                  placeholder="10"
                  value={periods}
                  onChange={(e) => setPeriods(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pmt1">Periodic Payment (optional)</Label>
                <Input
                  id="pmt1"
                  type="number"
                  placeholder="0"
                  value={pmt}
                  onChange={(e) => setPmt(e.target.value)}
                />
              </div>
              <Button onClick={calculateFV}>Calculate Future Value</Button>
            </TabsContent>

            <TabsContent value="pv" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fv">Future Value ($)</Label>
                <Input
                  id="fv"
                  type="number"
                  placeholder="16288.95"
                  value={fv}
                  onChange={(e) => setFv(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate2">Interest Rate (% per period)</Label>
                <Input
                  id="rate2"
                  type="number"
                  placeholder="5"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="periods2">Number of Periods</Label>
                <Input
                  id="periods2"
                  type="number"
                  placeholder="10"
                  value={periods}
                  onChange={(e) => setPeriods(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pmt2">Periodic Payment (optional)</Label>
                <Input
                  id="pmt2"
                  type="number"
                  placeholder="0"
                  value={pmt}
                  onChange={(e) => setPmt(e.target.value)}
                />
              </div>
              <Button onClick={calculatePV}>Calculate Present Value</Button>
            </TabsContent>
          </Tabs>

          <Button onClick={reset} variant="outline" className="w-full">Reset</Button>

          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Result:</strong> ${result.toFixed(2)}
                </p>
                <CopyResultButton value={result.toFixed(2)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["time-value-money"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
